import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, FileText, Eye, ArrowUpRight, Phone, Image, Quote, Users, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminDashboard() {
    const [stats, setStats] = useState({
        quotes: 0, newQuotes: 0, callbacks: 0, articles: 0, published: 0, projects: 0, testimonials: 0,
    });
    const [recent, setRecent] = useState([]);
    const [analytics, setAnalytics] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/admin/quotes").catch(() => ({ data: [] })),
            api.get("/admin/blog").catch(() => ({ data: [] })),
            api.get("/admin/callbacks").catch(() => ({ data: [] })),
            api.get("/admin/projects").catch(() => ({ data: [] })),
            api.get("/admin/testimonials").catch(() => ({ data: [] })),
            api.get("/admin/analytics/overview").catch(() => ({ data: null })),
        ]).then(([q, b, c, p, t, a]) => {
            const quotes = q.data || [];
            setStats({
                quotes: quotes.length,
                newQuotes: quotes.filter((x) => x.status === "new").length,
                callbacks: (c.data || []).filter((x) => x.status === "new").length,
                articles: (b.data || []).length,
                published: (b.data || []).filter((x) => x.published).length,
                projects: (p.data || []).filter((x) => x.published).length,
                testimonials: (t.data || []).filter((x) => x.featured).length,
            });
            setRecent(quotes.slice(0, 5));
            setAnalytics(a.data);
        });
    }, []);

    const tiles = [
        { label: "Nouvelles demandes devis", value: stats.newQuotes, icon: Inbox, to: "/admin/quotes", accent: stats.newQuotes > 0 },
        { label: "Rappels en attente", value: stats.callbacks, icon: Phone, to: "/admin/callbacks", accent: stats.callbacks > 0 },
        { label: "Réalisations publiées", value: stats.projects, icon: Image, to: "/admin/projects" },
        { label: "Articles publiés", value: stats.published, icon: FileText, to: "/admin/blog" },
        { label: "Témoignages actifs", value: stats.testimonials, icon: Quote, to: "/admin/testimonials" },
        { label: "Devis reçus (total)", value: stats.quotes, icon: Eye, to: "/admin/quotes" },
    ];

    return (
        <div className="p-10" data-testid="admin-dashboard">
            <span className="overline">Espace admin</span>
            <h1 className="mt-3 font-serif text-4xl text-brand-ink font-light">Tableau de bord</h1>

            {analytics && (
                <div className="mt-8 bg-brand-ink text-brand-bg p-8" data-testid="analytics-overview">
                    <div className="flex items-center gap-2 overline mb-5" style={{ color: "#A8C0B0" }}>
                        <BarChart3 size={12} /> Analytics — 30 derniers jours
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                        <Metric label="Visites 30j" value={analytics.views_30d} />
                        <Metric label="Visites 7j" value={analytics.views_7d} />
                        <Metric label="Devis 30j" value={analytics.quotes_30d} />
                        <Metric label="Rappels 30j" value={analytics.callbacks_30d} />
                        <Metric label="Taux conversion" value={`${analytics.conversion_rate_30d}%`} />
                    </div>
                    {analytics.top_pages?.length > 0 && (
                        <div className="mt-8 pt-6 border-t border-brand-bg/15">
                            <p className="text-xs uppercase tracking-[0.2em] text-brand-bg/60 mb-4">Pages les plus visitées</p>
                            <div className="space-y-2 max-w-2xl">
                                {analytics.top_pages.map((p) => (
                                    <div key={p.path} className="flex justify-between text-sm">
                                        <span className="truncate text-brand-bg/85">{p.path}</span>
                                        <span className="text-brand-sage font-medium">{p.views}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
                {tiles.map((t) => (
                    <Link
                        key={t.label}
                        to={t.to}
                        data-testid={`tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`p-6 border border-brand-border ${t.accent ? "bg-brand-sage text-brand-bg" : "bg-brand-bgalt text-brand-ink"} group transition-shadow hover:shadow-sm`}
                    >
                        <div className="flex items-center justify-between">
                            <t.icon size={18} strokeWidth={1.4} />
                            <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="mt-6 font-serif text-4xl font-light">{t.value}</p>
                        <p className="mt-2 text-xs uppercase tracking-[0.18em] opacity-80">{t.label}</p>
                    </Link>
                ))}
            </div>

            <div className="mt-12">
                <div className="flex items-end justify-between mb-6">
                    <h2 className="font-serif text-2xl text-brand-ink">Dernières demandes</h2>
                    <Link to="/admin/quotes" className="text-xs uppercase tracking-[0.2em] text-brand-sage hover:underline">Tout voir →</Link>
                </div>
                {recent.length === 0 ? (
                    <div className="py-12 text-center bg-brand-bgalt border border-brand-border">
                        <p className="font-serif text-xl text-brand-ink italic">Aucune demande pour l'instant.</p>
                    </div>
                ) : (
                    <div className="bg-brand-bgalt border border-brand-border divide-y divide-brand-border">
                        {recent.map((q) => (
                            <Link key={q.id} to="/admin/quotes" className="block p-5 hover:bg-brand-sage/5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-serif text-lg text-brand-ink">{q.first_name} {q.last_name}</p>
                                        <p className="text-sm text-brand-sageDark">{q.project_type} · {q.location}</p>
                                    </div>
                                    <span className={`text-xs uppercase tracking-[0.16em] px-3 py-1 ${q.status === "new" ? "bg-brand-sage text-brand-bg" : "border border-brand-border text-brand-sageDark"}`}>
                                        {q.status}
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Metric({ label, value }) {
    return (
        <div>
            <p className="font-serif text-3xl md:text-4xl font-light">{value}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-brand-bg/60">{label}</p>
        </div>
    );
}
